import { AnimatePresence, motion } from 'framer-motion';
import { LiteralUnion } from 'prettier';
import React from 'react';
import { SourceIndicator } from '.';
import { Layout } from '../../../queries/ats/layout/types';
import { useTitleProvider } from '../../../queries/ats/me/providers/hooks';
import { Provider } from '../../../queries/ats/me/providers/types';
import { DetailsSearchParams } from '../../../views/ats-details/use-details-search-params';
import { InputContext } from '../../../views/ats-details/use-input-context';
import { useSectionContext } from '../../../views/ats-pages/section/section-context';
import { ErrorBoundary } from '../../error-boundary';
import { HighlightText } from '../../highlight-text';
import { AtsImage } from '../AtsImage';
import { AtsItem } from '../AtsItem';
import css from './ats-asset.module.scss';
import { AtsTitleBadge } from './ats-title-badge';
import { AtsTitleProgress } from './ats-title-progress/ats-title-progress';
import { AtsItemOverwrites } from './atsAsset.interfaces';
import { getSubtitle } from './partials/atsAssetViewUtils';

function shouldIncludeExpired(section: LiteralUnion<`${Layout.SectionStyleType}`, string>) {
  switch (section) {
    case 'continue-watching':
    case 'bought-and-rented':
    case 'bought':
    case 'rented':
      return true;
    default:
      return false;
  }
}

function getParams(
  provider: Provider | undefined,
  params: Partial<InputContext> | undefined,
  section: LiteralUnion<`${Layout.SectionStyleType}`, string>,
): Partial<DetailsSearchParams> | undefined {
  if (provider === undefined) {
    return params;
  }

  return { orderByProviderId: provider.id, includeExpired: shouldIncludeExpired(section), ...params };
}

interface AtsTitleAssetProps {
  item: Layout.SectionItemTitle;
  pattern: Layout.Pattern;
  params?: Partial<InputContext>;
  overwrites?: AtsItemOverwrites;
  matchTitle?: string;
}

export function AtsTitleAsset({ item, pattern, params, overwrites, matchTitle }: AtsTitleAssetProps) {
  const section = useSectionContext('AtsItemTitleAsset');
  const { provider } = useTitleProvider(item.title);

  const subtitle = getSubtitle(item, pattern, section.style.type);
  const titleName =
    item.title.type === 'episode'
      ? `${item.title.episode.sequenceNumber}. ${item.title.titleName}`
      : item.title.titleName;
  const label = item.name || titleName;

  return (
    <AtsItem
      title={label}
      className={css.asset}
      onMouseDown={(e) => e.preventDefault()}
      item={item}
      pattern={pattern}
      data-pattern={pattern}
      overwrites={{ linkId: overwrites?.linkId }}
      params={getParams(provider, params, section.style.type)}
    >
      <motion.div className={css.imageContainer} layout>
        <AnimatePresence>
          <AtsTitleBadge title={item.title} />
        </AnimatePresence>

        {section.style.type === 'continue-watching' && item.title.type !== 'series' && (
          <ErrorBoundary fallback={() => null}>
            <AnimatePresence>
              <AtsTitleProgress title={item.title} />
            </AnimatePresence>
          </ErrorBoundary>
        )}

        <ErrorBoundary fallback={() => null}>
          <SourceIndicator title={item.title} />
        </ErrorBoundary>
        <AtsImage className={css.image} item={item} pattern={pattern} />
      </motion.div>

      <h3 className={css.title}>
        {matchTitle !== undefined ? <HighlightText highlight={matchTitle}>{label}</HighlightText> : label}
      </h3>
      {subtitle && <p className={css.subtitle}>{subtitle}</p>}
    </AtsItem>
  );
}
